import React, { useState, useEffect, useRef } from 'react';
import '../styles/despFooter.css';

// Declare feather as a global variable
declare global {
  interface Window {
    feather?: {
      replace: () => void;
    };
  }
}

interface DespFooterProps {
  webPortalUrl?: string;
}

const DespFooter: React.FC<DespFooterProps> = ({
  webPortalUrl = 'https://platform.destine.eu'
}) => {
  const [footerMenuIsVisible, setFooterMenuIsVisible] = useState(false);
  const [footerMenuIconState, setFooterMenuIconState] = useState('menu');
  const [footerHasRevealed, setFooterHasRevealed] = useState(true);

  const footerRef = useRef<HTMLDivElement>(null);
  const footerMenuRef = useRef<HTMLDivElement>(null);
  const footerScrollPositionPrec = useRef(0);
  const tempFooterPrevPos = useRef(0);
  const footerHidingTimeoutId = useRef<NodeJS.Timeout | null>(null);

  const footerHideTimeout = 4000;

  // Initialize feather icons
  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  }, []);

  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  }, [footerMenuIconState]);

  // Footer show/hide on scroll functionality
  useEffect(() => {
    const hideFooter = () => {
      const footer = footerRef.current;
      if (!footer) return;

      const body = document.querySelector('body');
      const footerHeight = getComputedStyle(body!).getPropertyValue('--footer-height');

      if (document.documentElement.scrollHeight !== window.innerHeight &&
          window.scrollY < (document.documentElement.scrollHeight - window.innerHeight - 50)) {
        setFooterHasRevealed(false);
        footer.style.transition = 'bottom 500ms ease-in-out, background-color 500ms ease-in-out';
        footer.style.bottom = "-" + footerHeight;
      }
    };

    const showFooter = () => {
      const footer = footerRef.current;
      if (!footer) return;

      footer.style.transition = 'bottom 250ms ease-in-out, background-color 500ms ease-in-out';
      footer.style.bottom = '0px';
      footer.style.position = 'fixed';
      setTimeout(() => {
        setFooterHasRevealed(true);
      }, 500);

      if (footerHidingTimeoutId.current) {
        clearTimeout(footerHidingTimeoutId.current);
      }
    };

    const handleScroll = () => {
      const deltaY = window.scrollY - tempFooterPrevPos.current;

      if (window.scrollY >= (document.documentElement.scrollHeight - window.innerHeight - 50)) {
        showFooter();
      } else {
        if (deltaY < 0 && deltaY > -100) {
          setTimeout(() => {
            footerScrollPositionPrec.current = window.scrollY;
          }, 500);

          if (footerHasRevealed) {
            if (footerHidingTimeoutId.current) {
              clearTimeout(footerHidingTimeoutId.current);
            }
            hideFooter();
            setTimeout(() => {
              setFooterMenuIconState('menu');
              setFooterMenuIsVisible(false);
              if (window.feather) {
                window.feather.replace();
              }
            }, 500);
          }
        } else if (deltaY > 0 && deltaY < 100) {
          if (window.scrollY - footerScrollPositionPrec.current > 0) {
            showFooter();
            footerHidingTimeoutId.current = setTimeout(() => {
              hideFooter();
            }, footerHideTimeout);
          }
        }
      }

      tempFooterPrevPos.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [footerHasRevealed, footerHideTimeout]);

  // Footer mouse events
  useEffect(() => {
    const footer = footerRef.current;
    const footerMenu = footerMenuRef.current;

    const handleFooterMouseOver = () => {
      if (footerHidingTimeoutId.current) {
        clearTimeout(footerHidingTimeoutId.current);
      }
    };

    const handleFooterMouseLeave = () => {
      if (footerHidingTimeoutId.current) {
        clearTimeout(footerHidingTimeoutId.current);
      }

      const body = document.querySelector('body');
      const footerHeight = getComputedStyle(body!).getPropertyValue('--footer-height');

      footerHidingTimeoutId.current = setTimeout(() => {
        if (footer) {
          footer.style.transition = 'bottom 500ms ease-in-out, background-color 500ms ease-in-out';
          footer.style.bottom = "-" + footerHeight;
        }
      }, footerHideTimeout);
    };

    const handleMenuMouseOver = () => {
      if (footerHidingTimeoutId.current) {
        clearTimeout(footerHidingTimeoutId.current);
      }
    };

    const handleMenuMouseLeave = () => {
      if (footerHidingTimeoutId.current) {
        clearTimeout(footerHidingTimeoutId.current);
      }

      const body = document.querySelector('body');
      const footerHeight = getComputedStyle(body!).getPropertyValue('--footer-height');

      footerHidingTimeoutId.current = setTimeout(() => {
        if (footer) {
          footer.style.transition = 'bottom 500ms ease-in-out, background-color 500ms ease-in-out';
          footer.style.bottom = "-" + footerHeight;
        }
      }, footerHideTimeout);
    };

    // Mouse position detection
    const handleMouseMove = (e: MouseEvent) => {
      if (e.clientY > window.innerHeight - 50) {
        const footerElement = footerRef.current;
        if (footerElement) {
          footerElement.style.transition = 'bottom 250ms ease-in-out, background-color 500ms ease-in-out';
          footerElement.style.bottom = '0px';
          footerElement.style.position = 'fixed';
          setTimeout(() => {
            setFooterHasRevealed(true);
          }, 500);

          if (footerHidingTimeoutId.current) {
            clearTimeout(footerHidingTimeoutId.current);
          }
        }
      }
    };

    if (footer) {
      footer.addEventListener('mouseover', handleFooterMouseOver);
      footer.addEventListener('mouseleave', handleFooterMouseLeave);
    }

    if (footerMenu) {
      footerMenu.addEventListener('mouseover', handleMenuMouseOver);
      footerMenu.addEventListener('mouseleave', handleMenuMouseLeave);
    }

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      if (footer) {
        footer.removeEventListener('mouseover', handleFooterMouseOver);
        footer.removeEventListener('mouseleave', handleFooterMouseLeave);
      }
      if (footerMenu) {
        footerMenu.removeEventListener('mouseover', handleMenuMouseOver);
        footerMenu.removeEventListener('mouseleave', handleMenuMouseLeave);
      }
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [footerHideTimeout]);

  const toggleFooterMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (footerMenuIsVisible) {
      setFooterMenuIconState('menu');
      setFooterMenuIsVisible(false);
    } else {
      setFooterMenuIconState('x');
      setFooterMenuIsVisible(true);
    }
  };

  return (
    <div id="footer-container" ref={footerRef}>
      <div id="footer-inner-container">
        {/* Left Container */}
        <div id="footer-left-container">
          <a href="https://platform.destine.eu">
            <img id="footer-desp-logo" alt="desp-home" src="/desp-assets/desp_logo.svg" />
          </a>
        </div>

        {/* Central Container */}
        <div id="footer-central-container">
          <a id="footer-destination-earth" href="https://platform.destine.eu">
            <img id="footer-destination-earth-logo" className="footer-logo" alt="destination earth" src="/desp-assets/destination-earth.png" />
          </a>
          <a id="footer-eu" href="https://european-union.europa.eu/">
            <img id="footer-eu-logo" className="footer-logo" alt="eu commission" src="/desp-assets/funded-by-EU.png" />
          </a>
          <a id="footer-implement-by">
            <img id="footer-implement-by-logo" className="footer-logo" alt="implemented by" src="/desp-assets/implemented-by.png" />
          </a>
          <a id="footer-ecmwf" href="https://www.ecmwf.int/">
            <img id="footer-ecmwf-logo" className="footer-logo" alt="ecmwf" src="/desp-assets/ecmwf.png" />
          </a>
          <a id="footer-esa" href="https://www.esa.int/">
            <img id="footer-esa-logo" className="footer-logo" alt="esa" src="/desp-assets/esa.png" />
          </a>
          <a id="footer-eumetsat" href="https://www.eumetsat.int/">
            <img id="footer-eumetsat-logo" className="footer-logo" alt="eumetsat" src="/desp-assets/eumetsat.png" />
          </a>
        </div>

        {/* Right Container */}
        <div id="footer-right-container" onClick={toggleFooterMenu}>
          <i
            data-feather={footerMenuIconState}
            id="footer-menu-icon"
          ></i>
        </div>
      </div>

      {/* Footer menu */}
      <div
        id="footer-menu"
        ref={footerMenuRef}
        style={{
          display: footerMenuIsVisible ? 'flex' : 'none',
          opacity: footerMenuIsVisible ? 1 : 0
        }}
      >
        <a id="footer-code-conduct" className="frame-link" href="https://platform.destine.eu/code-of-conduct/">Code of Conduct</a>
        <a id="footer-terms-conditions" className="frame-link" href="https://platform.destine.eu/terms-and-conditions/">Terms and Conditions</a>
        <a id="footer-privacy-policies" className="frame-link" href="https://platform.destine.eu/privacy-policies/">Privacy Policies</a>
        <a id="footer-legal-notice" className="frame-link" href="https://platform.destine.eu/legal-notice/">Legal Notice</a>
        <div className="mobile-link">
          <hr />
          <a id="footer-destination-earth-mobile" className="footer-frame-link" href="https://platform.destine.eu">
            <img className="mobile-footer-logo" alt="destination earth" src="/desp-assets/destination-earth.png" />
          </a>
          <a id="footer-eu-mobile" className="footer-frame-link" href="https://european-union.europa.eu/">
            <img className="mobile-footer-logo" alt="eu commission" src="/desp-assets/funded-by-EU.png" />
          </a>
          <a id="footer-implemented-by-mobile" className="footer-frame-link">
            <img className="mobile-footer-logo" alt="implemented by" src="/desp-assets/implemented-by.png" />
          </a>
          <a id="footer-ecmwf-mobile" className="footer-frame-link" href="https://www.ecmwf.int/">
            <img className="mobile-footer-logo" alt="ecmwf" src="/desp-assets/ecmwf.png" />
          </a>
          <a id="footer-esa-mobile" className="footer-frame-link" href="https://www.esa.int/">
            <img className="mobile-footer-logo" alt="esa" src="/desp-assets/esa.png" />
          </a>
          <a id="footer-eumetsat-mobile" className="footer-frame-link" href="https://www.eumetsat.int/">
            <img className="mobile-footer-logo" alt="eumetsat" src="/desp-assets/eumetsat.png" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default DespFooter;
